/**
 * Verifies the published certificates and registration records against the
 * registrar's root CA (`certificate_authority_uri`), using only WebCrypto so
 * any monitor can reproduce the check. The root certifies a sub CA per
 * purpose, which signs the certificates; a registrar with a single CA signs
 * them with the anchor itself. Both paths are accepted. A CA certificate
 * between the anchor and a certificate is taken from `x5c[1]` if present
 * (earlier registrars sent their issuing CA there), otherwise fetched from
 * the certificate's caIssuers URL:
 *
 * - access certificates: X.509 signature (ecdsa-with-SHA256) by the anchor,
 *   or by a sub CA the anchor certified;
 * - registration certificates: the JWS signer (`x5c[0]`) is the anchor
 *   itself, or an end-entity certificate issued by a sub CA, and the ES256
 *   signature verifies with its key;
 * - registration records: the JWS signer (`x5c[0]`) is a statement signer,
 *   i.e. issued by the anchor or by a sub CA, with the extended key usage
 *   id-kp-documentSigning, and the ES256 signature verifies with its key.
 */

export type SignatureCheck =
  | "valid"
  | "invalid"
  | "unknown_signer"
  /** Registration records: the signer is certified, but not for statements. */
  | "wrong_key_usage"
  | "unsupported"
  | "unchecked";

export interface TrustAnchor {
  der: Uint8Array;
  key: CryptoKey;
  /**
   * Loads a CA certificate (DER) from a caIssuers URL. Without it, only
   * certificates signed by the anchor itself can be attributed.
   */
  fetchCertificate?: (url: string) => Promise<Uint8Array<ArrayBuffer>>;
}

const OID_ECDSA_WITH_SHA256 = "1.2.840.10045.4.3.2";
const OID_EXTENDED_KEY_USAGE = "2.5.29.37";
const OID_BASIC_CONSTRAINTS = "2.5.29.19";
const OID_AUTHORITY_INFO_ACCESS = "1.3.6.1.5.5.7.1.1";
const OID_CA_ISSUERS = "1.3.6.1.5.5.7.48.2";
/** id-kp-documentSigning (RFC 9336), the usage of statement signing keys. */
const OID_DOCUMENT_SIGNING = "1.3.6.1.5.5.7.3.36";
const ECDSA_P256 = { name: "ECDSA", namedCurve: "P-256" } as const;
const ECDSA_SHA256 = { name: "ECDSA", hash: "SHA-256" } as const;

export async function loadTrustAnchor(pem: string): Promise<TrustAnchor> {
  const der = base64ToBytes(
    pem.replace(/-----(BEGIN|END) CERTIFICATE-----/g, "").replace(/\s+/g, ""),
  );
  return { der, key: await publicKey(der) };
}

export async function verifyAccessCertificate(
  derBase64: string,
  anchor: TrustAnchor,
): Promise<SignatureCheck> {
  try {
    const der = base64ToBytes(derBase64);
    const direct = await verifyIssuedBy(der, anchor.key);
    if (direct !== "invalid" || !anchor.fetchCertificate) {
      return direct;
    }
    const url = caIssuersUrl(der);
    if (!url) {
      return "invalid";
    }
    const issuer = await fetchedIssuingCa(url, anchor);
    if (!issuer) {
      return "invalid";
    }
    return await verifyIssuedBy(der, issuer);
  } catch {
    return "invalid";
  }
}

export async function verifyRegistrationCertificate(
  jws: string,
  anchor: TrustAnchor,
): Promise<SignatureCheck> {
  try {
    const [header, payload, signature] = jws.split(".");
    const protectedHeader = JSON.parse(
      new TextDecoder().decode(base64UrlToBytes(header)),
    ) as { alg?: string; x5c?: string[] };
    if (protectedHeader.alg !== "ES256") {
      return "unsupported";
    }
    const chain = (protectedHeader.x5c ?? []).map(base64ToBytes);
    if (chain.length === 0) {
      return "unknown_signer";
    }
    let key: CryptoKey;
    if (equalBytes(chain[0], anchor.der)) {
      key = anchor.key;
    } else {
      const issued = await issuedThroughChain(chain, anchor);
      if (issued !== "valid") {
        return issued === "unsupported" ? "unsupported" : "unknown_signer";
      }
      if (isCa(chain[0])) {
        return "unknown_signer";
      }
      key = await publicKey(chain[0]);
    }
    const valid = await crypto.subtle.verify(
      ECDSA_SHA256,
      key,
      base64UrlToBytes(signature),
      new TextEncoder().encode(`${header}.${payload}`),
    );
    return valid ? "valid" : "invalid";
  } catch {
    return "invalid";
  }
}

/**
 * A registration record is signed with the registrar's statement key, not
 * with a CA key: `x5c[0]` must be issued by the trust anchor, directly or
 * through a sub CA, and certified for document signing.
 * Without the usage check any certificate of the CA, such as a WRP's access
 * certificate, could sign a record.
 */
export async function verifyRegistrationRecord(
  jws: string,
  anchor: TrustAnchor,
): Promise<SignatureCheck> {
  try {
    const [header, payload, signature] = jws.split(".");
    const protectedHeader = JSON.parse(
      new TextDecoder().decode(base64UrlToBytes(header)),
    ) as { alg?: string; x5c?: string[] };
    if (protectedHeader.alg !== "ES256") {
      return "unsupported";
    }
    const chain = (protectedHeader.x5c ?? []).map(base64ToBytes);
    if (chain.length === 0) {
      return "unknown_signer";
    }
    const issued = await issuedThroughChain(chain, anchor).catch(
      () => "invalid" as const,
    );
    if (issued !== "valid") {
      return issued === "unsupported" ? "unsupported" : "unknown_signer";
    }
    const der = chain[0];
    if (!extendedKeyUsages(der).includes(OID_DOCUMENT_SIGNING)) {
      return "wrong_key_usage";
    }
    const valid = await crypto.subtle.verify(
      ECDSA_SHA256,
      await publicKey(der),
      base64UrlToBytes(signature),
      new TextEncoder().encode(`${header}.${payload}`),
    );
    return valid ? "valid" : "invalid";
  } catch {
    return "invalid";
  }
}

/**
 * `chain[0]` is issued by the anchor, or by a CA certificate the anchor
 * issued: `chain[1]` (registrars of earlier versions sent their issuing CA
 * there), or else the sub CA named by the caIssuers URL of `chain[0]`, since
 * a registrar sends only the signer certificate. Longer paths are not part
 * of the registrar's hierarchy.
 */
async function issuedThroughChain(
  chain: Uint8Array<ArrayBuffer>[],
  anchor: TrustAnchor,
): Promise<SignatureCheck> {
  const direct = await verifyIssuedBy(chain[0], anchor.key);
  if (direct !== "invalid") {
    return direct;
  }
  if (chain.length >= 2) {
    const issuer = await issuingCa(chain[1], anchor);
    return issuer ? verifyIssuedBy(chain[0], issuer) : "invalid";
  }
  const url = anchor.fetchCertificate ? caIssuersUrl(chain[0]) : undefined;
  const issuer = url ? await fetchedIssuingCa(url, anchor) : undefined;
  return issuer ? verifyIssuedBy(chain[0], issuer) : "invalid";
}

/** Issuing CAs loaded from caIssuers URLs, once per anchor and URL. */
const fetchedIssuers = new WeakMap<
  TrustAnchor,
  Map<string, Promise<CryptoKey | undefined>>
>();

function fetchedIssuingCa(
  url: string,
  anchor: TrustAnchor,
): Promise<CryptoKey | undefined> {
  let cache = fetchedIssuers.get(anchor);
  if (!cache) {
    cache = new Map();
    fetchedIssuers.set(anchor, cache);
  }
  let issuer = cache.get(url);
  if (!issuer) {
    issuer = anchor.fetchCertificate!(url)
      .then((der) => issuingCa(der, anchor))
      .catch(() => undefined);
    cache.set(url, issuer);
  }
  return issuer;
}

/** The key of `der` if it is a CA certificate issued by the anchor. */
async function issuingCa(
  der: Uint8Array<ArrayBuffer>,
  anchor: TrustAnchor,
): Promise<CryptoKey | undefined> {
  if (!isCa(der) || (await verifyIssuedBy(der, anchor.key)) !== "valid") {
    return undefined;
  }
  return publicKey(der);
}

function publicKey(der: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "spki",
    subjectPublicKeyInfo(der),
    ECDSA_P256,
    false,
    ["verify"],
  );
}

/** X.509 signature of the certificate by `key` (ecdsa-with-SHA256). */
async function verifyIssuedBy(
  der: Uint8Array<ArrayBuffer>,
  key: CryptoKey,
): Promise<SignatureCheck> {
  const [tbs, algorithm, signature] = children(der, readTlv(der, 0));
  const [oid] = children(der, algorithm);
  if (decodeOid(der.subarray(oid.start, oid.end)) !== OID_ECDSA_WITH_SHA256) {
    return "unsupported";
  }
  // BIT STRING: first content byte is the number of unused bits (0).
  const ecdsaSignature = der.subarray(signature.start + 1, signature.end);
  const valid = await crypto.subtle.verify(
    ECDSA_SHA256,
    key,
    ecdsaDerToRaw(ecdsaSignature),
    der.subarray(tbs.headerStart, tbs.end),
  );
  return valid ? "valid" : "invalid";
}

// --- DER helpers ---------------------------------------------------------------

interface Tlv {
  tag: number;
  headerStart: number;
  start: number;
  end: number;
}

function readTlv(bytes: Uint8Array, offset: number): Tlv {
  const tag = bytes[offset];
  let length = bytes[offset + 1];
  let start = offset + 2;
  if (length & 0x80) {
    const count = length & 0x7f;
    length = 0;
    for (let index = 0; index < count; index++) {
      length = length * 256 + bytes[start + index];
    }
    start += count;
  }
  if (start + length > bytes.length) {
    throw new Error("Truncated DER");
  }
  return { tag, headerStart: offset, start, end: start + length };
}

function children(bytes: Uint8Array, parent: Tlv): Tlv[] {
  const result: Tlv[] = [];
  for (let offset = parent.start; offset < parent.end;) {
    const tlv = readTlv(bytes, offset);
    result.push(tlv);
    offset = tlv.end;
  }
  return result;
}

/**
 * tbsCertificate: [0] version?, serial, signature, issuer, validity,
 * subject, subjectPublicKeyInfo, [1]?, [2]?, [3] extensions?
 */
function tbsFields(der: Uint8Array): Tlv[] {
  return children(der, children(der, readTlv(der, 0))[0]);
}

function subjectPublicKeyInfo(
  der: Uint8Array<ArrayBuffer>,
): Uint8Array<ArrayBuffer> {
  const fields = tbsFields(der);
  const spki = fields[(fields[0].tag === 0xa0 ? 1 : 0) + 5];
  return der.subarray(spki.headerStart, spki.end);
}

/** The value (inside the OCTET STRING) of an extension, if present. */
function extensionValue(der: Uint8Array, oid: string): Tlv | undefined {
  const wrapper = tbsFields(der).find((field) => field.tag === 0xa3);
  const extensions = wrapper ? children(der, children(der, wrapper)[0]) : [];
  for (const extension of extensions) {
    const parts = children(der, extension);
    if (decodeOid(der.subarray(parts[0].start, parts[0].end)) === oid) {
      return readTlv(der, parts[parts.length - 1].start);
    }
  }
  return undefined;
}

/** The key purposes of the extended key usage extension, if present. */
function extendedKeyUsages(der: Uint8Array): string[] {
  // SEQUENCE OF KeyPurposeId.
  const value = extensionValue(der, OID_EXTENDED_KEY_USAGE);
  return value
    ? children(der, value).map((purpose) =>
        decodeOid(der.subarray(purpose.start, purpose.end)),
      )
    : [];
}

/** basicConstraints cA (SEQUENCE { cA BOOLEAN DEFAULT FALSE, ... }). */
function isCa(der: Uint8Array): boolean {
  const value = extensionValue(der, OID_BASIC_CONSTRAINTS);
  const [first] = value ? children(der, value) : [];
  return first?.tag === 0x01 && der[first.start] !== 0;
}

/** The first caIssuers URI of the authority information access extension. */
function caIssuersUrl(der: Uint8Array): string | undefined {
  const value = extensionValue(der, OID_AUTHORITY_INFO_ACCESS);
  for (const description of value ? children(der, value) : []) {
    const [method, location] = children(der, description);
    if (
      decodeOid(der.subarray(method.start, method.end)) === OID_CA_ISSUERS &&
      location.tag === 0x86
    ) {
      // uniformResourceIdentifier [6] IA5String
      return new TextDecoder().decode(
        der.subarray(location.start, location.end),
      );
    }
  }
  return undefined;
}

function decodeOid(bytes: Uint8Array): string {
  const parts = [Math.floor(bytes[0] / 40), bytes[0] % 40];
  let value = 0;
  for (let index = 1; index < bytes.length; index++) {
    value = value * 128 + (bytes[index] & 0x7f);
    if (!(bytes[index] & 0x80)) {
      parts.push(value);
      value = 0;
    }
  }
  return parts.join(".");
}

/** ECDSA-Sig-Value (DER SEQUENCE of r, s) to the raw r || s WebCrypto expects. */
function ecdsaDerToRaw(der: Uint8Array): Uint8Array<ArrayBuffer> {
  const [r, s] = children(der, readTlv(der, 0));
  const raw = new Uint8Array(64);
  for (const [index, integer] of [r, s].entries()) {
    let value = der.subarray(integer.start, integer.end);
    while (value.length > 32 && value[0] === 0) value = value.subarray(1);
    raw.set(value, index * 32 + (32 - value.length));
  }
  return raw;
}

function equalBytes(left: Uint8Array, right: Uint8Array): boolean {
  return (
    left.length === right.length &&
    left.every((byte, index) => byte === right[index])
  );
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  return base64ToBytes(value.replace(/-/g, "+").replace(/_/g, "/"));
}

function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.padEnd(
    value.length + ((4 - (value.length % 4)) % 4),
    "=",
  );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
